/*
 * BUDGET.JSX - Stage Accordion Budget Component with Global User Permissions
 * 
 * 
 * SUMMARY:
 * Budget accordion component that displays project stage budget information with
 * enhanced permission controls for global users during quote processing states.
 * Renders task budget breakdowns with conditional edit/view access based on user privileges.
 * 
 * ATTRIBUTION FLAG SUPPORT:
 * - Allows flagged sections (`is_attribution_level`) to expand so users can browse child forecast values.
 * - Hides all Actual-related fields for descendant sections/tasks when an attribution flag exists in the hierarchy.
 * 
 * PROPS:
 * - inProgressQuote: Boolean indicating quote processing state
 * - isGlobalUser: Boolean indicating global user privileges
 * - updatePermission: Boolean indicating edit privileges
 */
import React, { useState, Fragment } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Collapse,
  Table,
} from 'react-bootstrap';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toUSD, toPercent, getClaimProfitPct, getClaimProfitAmount } from '../../util/currency-formatter';
import EditIcon from '@mui/icons-material/Edit';
import _ from 'lodash';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toDecimal, roundValue } from '../../util/decimal-utils';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

const StageAccordionBudget = ({
  stage,
  projectQuoteType,
  calculations,
  variations = [],
  quoteType,
  handleEditClick,
  progressClaims,
  navigateToCostBreakdown,
  inProgressQuote = false,
  isGlobalUser = false,
  updatePermission,
  isParentFlagged = false
}) => {
  const [openStage, setOpenStage] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [openTasks, setOpenTasks] = useState({});
  const toggleTask = (taskId) => {
    setOpenTasks((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const toggleStage = () => {
    if (!hasBeenOpened) {
      setHasBeenOpened(true);
    }
    setOpenStage((prev) => !prev);
  };

  // Child rows (tasks + subtables) must hide Actual-related fields when:
  // - this stage is under a flagged section, or
  // - this stage itself is flagged (its tasks are children of the flagged section)
  const hideActualsInChildRows = isParentFlagged || stage.is_attribution_level;

  const getBudgetClass = (difference) => {
    if (difference > 0) return 'budget-under';
    if (difference < 0) return 'budget-exceed';
    return '';
  };
  const getApprovedClaimAmountByTask = (taskId) =>
    progressClaims
      .filter(
        (progressClaim) =>
          progressClaim.progressClaimStatus === 'Approved' ||
          progressClaim.progressClaimStatus === 'Finalised'
      )
      .reduce((totalClaimAmount, progressClaim) => {
        const matchingTask = progressClaim.progressClaimTask.find(
          (task) => task.taskId === taskId
        );
        return matchingTask
          ? totalClaimAmount + (matchingTask.total || 0)
          : totalClaimAmount;
      }, 0);

  /**
   * Collects all descendant stage IDs and task IDs from a section hierarchy.
   * Used for aggregating both section-level and task-level claims in a tree.
   */
  const getHierarchyIds = (s) => {
    let taskIds = [];
    let stageIds = [s.id];

    if (s.tasks && Array.isArray(s.tasks)) taskIds.push(...s.tasks.map(t => t.id));
    if (s.stages && Array.isArray(s.stages)) {
      s.stages.forEach(nested => {
        const childIds = getHierarchyIds(nested);
        taskIds.push(...childIds.taskIds);
        stageIds.push(...childIds.stageIds);
      });
    }
    return { taskIds, stageIds };
  };

  /**
   * Gets the total approved claim amount for any section (attribution-level or parent).
   * Aggregates both section-level claims (for this section and its descendants)
   * and historical task-level claims from the hierarchy.
   */
  const getApprovedClaimAmountBySection = (stageData) => {
    const { taskIds, stageIds } = getHierarchyIds(stageData);

    return progressClaims
      .filter(
        (progressClaim) =>
          progressClaim.progressClaimStatus === 'Approved' ||
          progressClaim.progressClaimStatus === 'Finalised'
      )
      .reduce((totalClaimAmount, progressClaim) => {
        const claimTasks = progressClaim.progressClaimTask || [];

        // Find all records belonging to this hierarchy in this claim
        const matchingRecords = claimTasks.filter(t =>
          (t.stageId && stageIds.includes(t.stageId)) ||
          (t.taskId && taskIds.includes(t.taskId))
        );

        if (matchingRecords.length > 0) {
          /**
           * Logic for accurate aggregation:
           * For each branch in the hierarchy, we might have:
           * 1. A section-level claim (type === 'SECTION')
           * 2. Multiple task-level claims
           * 
           * If a section-level claim exists for a stage, it represents the total for that whole stage
           * (tasks are already rolled into it). So we prefer 'SECTION' records to avoid double counting
           * tasks that might also be present in the claim.
           */

          // First, identify the specific stages that have section claims in this progress claim
          const stagesWithSectionClaims = new Set(
            matchingRecords.filter(t => t.type === 'SECTION').map(t => t.stageId)
          );

          matchingRecords.forEach(record => {
            if (record.type === 'SECTION') {
              // Always include section claims
              totalClaimAmount += parseFloat(record.total || 0);
            } else if (record.taskId) {
              // For task claims, only include them if their direct parent stage 
              // DOES NOT have a section-level claim in this same progress claim.
              // If the parent stage HAS a section claim, the task claim is already included in that total.
              const directParentStageId = record.stageId; // This is the stageId assigned to the task record
              if (!stagesWithSectionClaims.has(directParentStageId)) {
                totalClaimAmount += parseFloat(record.total || 0);
              }
            }
          });
        }

        return totalClaimAmount;
      }, 0);
  };

  const collectTasks = (stage) => {
    let tasks = [];
    if (stage.tasks && stage.tasks.length > 0) {
      tasks = tasks.concat(stage.tasks);
    }
    if (stage.stages && Array.isArray(stage.stages)) {
      stage.stages.forEach((nestedStage) => {
        tasks = tasks.concat(collectTasks(nestedStage));
      });
    }
    return tasks;
  };
  const combineProjectData = (stage) => {
    const isArray = _.isArray(stage);
    const sectionData = _.cloneDeep(stage) || [];
    const modifiedSectionData = [sectionData].map((section) => {
      if (!section.tasks) section.tasks = [];

      variations.forEach((variation) => {
        if (variation.stageId === section.id) {
          section.tasks.push(variation);
        }
      });
      return section;
    });

    return isArray ? modifiedSectionData : modifiedSectionData[0];
  };
  const headerData = combineProjectData(stage);
  const aggregatedTasks =
    stage.stages && stage.stages.length > 0
      ? collectTasks(stage)
      : stage.tasks || [];
  const aggregatedHeaderTasks =
    stage.stages && stage.stages.length > 0
      ? collectTasks(headerData)
      : headerData.tasks || [];

  const calculateBudget = (task) => {
    const budget =
      parseFloat(task.actualMaterialCost || 0) +
      parseFloat(task.actualLaborCost || 0) +
      parseFloat(task.actualSubContractorCost || 0);
    return budget || 0;
  };

  // const calculateAverageCompletion = (tasks) => {
  //   if (!tasks || tasks.length === 0) return '0.0%';
  //   const totalCompletion = getCompletionPercentages({
  //     data: progressClaims,
  //     taskIds: tasks.map((task) => task.id),
  //   });
  //   return `${(totalCompletion / tasks.length).toFixed(1)}%`;
  // };

  const calculateTotalMetrics = (tasks) => {
    let sumTotal = toDecimal(0),
      sumSubtotal = toDecimal(0),
      sumCommitted = toDecimal(0),
      sumBudget = toDecimal(0),
      sumProfit = toDecimal(0),
      sumDifference = toDecimal(0),
      sumClaim = toDecimal(0);
    (tasks || []).forEach((task) => {
      const materialSale = toDecimal(task.materialTotal_SaleAmount || 0);
      const laborSale = toDecimal(task.laborTotal_SaleAmount || 0);
      const subcontractorSale = toDecimal(task.subcontractorTotal_SaleAmount || 0);
      const materialCost = toDecimal(task.materialTotal_Cost || 0);
      const laborCost = toDecimal(task.laborTotal_Cost || 0);
      const subcontractorCost = toDecimal(task.subcontractorTotal_Cost || 0);
      const profitMarginCost = toDecimal(task.profitMarginCost || 0);

      const taskTotal = materialSale.plus(laborSale).plus(subcontractorSale);
      const taskSubtotal = materialCost.plus(laborCost).plus(subcontractorCost);
      const committed = toDecimal(task.committedCost || 0);
      const budget = toDecimal(task.actualMaterialCost || 0)
        .plus(toDecimal(task.actualLaborCost || 0))
        .plus(toDecimal(task.actualSubContractorCost || 0));

      sumTotal = sumTotal.plus(taskTotal);
      sumSubtotal = sumSubtotal.plus(taskSubtotal);
      sumCommitted = sumCommitted.plus(committed);
      sumBudget = sumBudget.plus(budget);
      sumProfit = sumProfit.plus(profitMarginCost);
      sumDifference = sumDifference.plus(taskTotal.minus(budget));
      sumClaim = sumClaim.plus(toDecimal(getApprovedClaimAmountByTask(task.id) || 0));
    });
    return {
      total: sumTotal.toNumber(),
      subtotal: sumSubtotal.toNumber(),
      committedCost: sumCommitted.toNumber(),
      actualCost: sumBudget.toNumber(),
      total_profit: sumProfit.toNumber(),
      difference: sumDifference.toNumber(),
      claimAmount: sumClaim.toNumber(),
    };
  };

  // const averageMetrics = calculateAverageMetrics(aggregatedTasks, stage);
  const totalHeaderMetrics = calculateTotalMetrics(
    aggregatedHeaderTasks,
    stage
  );

  // ALWAYS use section-level claim calculation for ALL sections
  // This correctly handles:
  // 1. Attribution-level sections with section claims
  // 2. Parent sections that contain nested attribution-level children
  // 3. Historical task-level claims made before sections were converted to attribution level
  // The getApprovedClaimAmountBySection function aggregates both section claims and task claims
  totalHeaderMetrics.claimAmount = getApprovedClaimAmountBySection(stage);

  const totalMetrics = calculateTotalMetrics(aggregatedTasks);

  /**
   * ATTRIBUTION-AWARE STAGE HEADER ACTUALS & COMMITTED COSTS
   *
   * When a stage contains a flagged attribution section somewhere in its subtree,
   * we must prevent parent headers from “leaking” hidden child task actuals/committeds.
   *
   * RULE:
   * - If a stage is flagged, its subtree actuals/committeds are represented ONLY by its direct attribution totals.
   * - Otherwise, actuals/committeds are the sum of:
   *   - direct tasks (and variations attached to this stage), plus
   *   - attribution-aware actuals/committeds of child stages.
   *
   * Forecast header values continue to come from aggregated descendant tasks.
   */
  const sumNumber = (v) => parseFloat(v) || 0;
  const getDirectAttributedActualCost = (stageNode) =>
    sumNumber(stageNode?.attributedActualMaterialCost) +
    sumNumber(stageNode?.attributedActualLaborCost) +
    sumNumber(stageNode?.attributedActualSubContractorCost);

  const getDirectAttributedCommittedCost = (stageNode) =>
    sumNumber(stageNode?.attributedCommittedCost) > 0
      ? sumNumber(stageNode?.attributedCommittedCost)
      : (sumNumber(stageNode?.attributedCommittedMaterialCost) + sumNumber(stageNode?.attributedCommittedSubContractorCost));

  const getStageTasksIncludingVariations = (stageNode) => {
    const combined = combineProjectData(stageNode);
    return combined?.tasks || [];
  };

  /**
   * Computes attribution-aware metric totals for budget section headers.
   *
   * @param {Object} stageNode - Target stage node object.
   * @param {Function} getDirectValue - Function returning direct attributed metric for flagged stages.
   * @param {Function} getTaskValue - Function returning metric value for non-flagged task items.
   * @returns {number} Aggregated metric total.
   */
  const calculateAttributionAwareMetric = (stageNode, getDirectValue, getTaskValue) => {
    if (!stageNode) return 0;
    if (stageNode.is_attribution_level) return getDirectValue(stageNode);

    let total = 0;
    const tasks = getStageTasksIncludingVariations(stageNode);
    (tasks || []).forEach((task) => {
      total += getTaskValue(task);
    });

    const childStages = Array.isArray(stageNode.stages) ? stageNode.stages : [];
    childStages.forEach((child) => {
      total += calculateAttributionAwareMetric(child, getDirectValue, getTaskValue);
    });

    return total;
  };

  const attributionAwareHeaderActualCost = calculateAttributionAwareMetric(
    stage,
    getDirectAttributedActualCost,
    (task) => sumNumber(task?.actualMaterialCost) + sumNumber(task?.actualLaborCost) + sumNumber(task?.actualSubContractorCost)
  );

  const attributionAwareHeaderCommittedCost = calculateAttributionAwareMetric(
    stage,
    getDirectAttributedCommittedCost,
    (task) => sumNumber(task?.committedCost)
  );

  const variationsForStage = variations.filter((v) => v.stageId === stage.id);
  const handleEdit = (item) => {
    if (item.isVariation || item.isOption) {
      handleEditClick(item, 'VARIATION', '', '', '', item.id);
    } else {
      handleEditClick(item, 'TASK', stage.id, item.id);
    }
  };
  const renderTaskRow = (task) => {
    const taskMaterialSale = parseFloat(task.materialTotal_SaleAmount) || 0;
    const taskLaborSale = parseFloat(task.laborTotal_SaleAmount) || 0;
    const taskSubcontractorSale =
      parseFloat(task.subcontractorTotal_SaleAmount) || 0;
    const taskMaterialCost = parseFloat(task.materialTotal_Cost) || 0;
    const taskLaborCost = parseFloat(task.laborTotal_Cost) || 0;
    const taskSubcontractorCost = parseFloat(task.subcontractorTotal_Cost) || 0;
    const taskProfitMarginCost = parseFloat(task.profitMarginCost) || 0;

    const taskTotal = {
      total: taskMaterialSale + taskLaborSale + taskSubcontractorSale,
      subtotal: taskMaterialCost + taskLaborCost + taskSubcontractorCost,
      total_profit: taskProfitMarginCost,
      material: {
        sale: taskMaterialSale,
        cost: taskMaterialCost,
        profit: taskMaterialSale - taskMaterialCost,
      },
      labor: {
        sale: taskLaborSale,
        cost: taskLaborCost,
        profit: taskLaborSale - taskLaborCost,
      },
      subcontractor: {
        sale: taskSubcontractorSale,
        cost: taskSubcontractorCost,
        profit: taskSubcontractorSale - taskSubcontractorCost,
      },
    };
    const claimAmount = roundValue(getApprovedClaimAmountByTask(task.id));
    let completionPercentage = '0.00';

    // Check that taskTotal.total is a non-zero number
    if (taskTotal.total) {
      const calculatedPercentage = (claimAmount / taskTotal.total) * 100;
      // Assign only if the result is a valid number
      if (!isNaN(calculatedPercentage)) completionPercentage = calculatedPercentage;
    }

    const budget = calculateBudget(task);
    return (
      <Fragment key={task.id}>
        <tr>
          <td>
            <div class='d-flex justify-content-between'>
              <div>
                <span>
                  {task.variationNo && `${task.variationNo} -`} {task.name}
                </span>
              </div>

              <div className='expand-icon align-items-center d-flex gap-2'>
                {(inProgressQuote && !isGlobalUser && !updatePermission) ? (
                  <VisibilityIcon
                    fontSize='small'
                    className='view-icon'
                    onClick={() => handleEdit(task)}
                  />
                ) : (
                  <EditIcon
                    fontSize='small'
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                    onClick={() => handleEdit(task)}
                  />
                )}

                <ExpandMoreIcon
                  onClick={() => toggleTask(task.id)}
                  aria-controls={`task-collapse-${task.id}`}
                  aria-expanded={openTasks[task.id] || false}
                />
              </div>
            </div>
          </td>
          <td className={`${hideActualsInChildRows ? 'invisible' : ''}`} data-cy="completion-percentage">{roundValue(completionPercentage, 0)}%</td>
          <td className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`} data-cy="claim-amount">
            {toUSD(getApprovedClaimAmountByTask(task.id))}
          </td>
          <td className='text-right' data-cy='sale-amount'>{toUSD(taskTotal.total)}</td>
          <td className='text-right' data-cy='forecast-cost'>{toUSD(taskTotal.subtotal)}</td>
          <td className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`} data-cy='committed-cost'>
            {toUSD(task.committedCost || 0)}
          </td>
          <td className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`} data-cy='actual-cost'>
            {navigateToCostBreakdown ? (
              <span
                className='actual-cost-link'
                onClick={() => navigateToCostBreakdown('all', task.id)}
              >
                {toUSD(budget)}
              </span>
            ) : (
              toUSD(budget)
            )}
          </td>
          <td className='text-right' data-cy='gross-profit-forecast'>{toUSD(taskTotal.total_profit)}</td>
          <td
            className={`text-right ${hideActualsInChildRows ? 'invisible' : ''} ${getApprovedClaimAmountByTask(task.id) - budget >= 0
              ? getApprovedClaimAmountByTask(task.id) - budget > 0
                ? 'budget-under'
                : ''
              : 'budget-exceed'
              }`}
            data-cy='gross-profit-actual'
          >
            {toUSD(getClaimProfitAmount(getApprovedClaimAmountByTask(task.id), budget))}
          </td>
        </tr>
        <tr>
          <td colSpan={9} className='p-0 border-0'>
            <Collapse in={openTasks[task.id]}>
              <div id={`task-collapse-${task.id}`}>
                <Table
                  bordered
                  className='table-create table-material table-material-ch table table-bordered'
                  data-cy={'budget-task-table-content'}
                >
                  <thead>
                    <tr>
                      <th></th>
                      <th></th>
                      <th className='text-right'></th>
                      <th className='text-right'></th>
                      <th className='text-right'></th>
                      <th className='text-right'></th>
                      <th className='text-right'></th>
                      <th className='text-right'></th>
                      <th className='text-right'></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className='p-0'></td>
                      <td className='p-0'></td>
                      <td className='collapse-title text-right'>
                        <span>Material</span>
                      </td>
                      <td className='text-right'>
                        {toUSD(taskTotal.material.sale)}
                      </td>
                      <td className='text-right'>
                        {toUSD(taskTotal.material.cost)}
                      </td>
                      <td className='text-right'>
                        {hideActualsInChildRows ? '' : toUSD(task.committedMaterialCost || 0)}
                      </td>
                      <td className={`text-right`}>
                        {hideActualsInChildRows ? '' : navigateToCostBreakdown ? (
                          <span
                            className='actual-cost-link'
                            onClick={() =>
                              navigateToCostBreakdown('material', task.id)
                            }
                          >
                            {toUSD(task.actualMaterialCost || 0)}
                          </span>
                        ) : (
                          toUSD(task.actualMaterialCost || 0)
                        )}
                      </td>
                      <td className='text-right'>
                        {toUSD(taskTotal.material.profit)}
                      </td>
                      <td
                        className={`${getBudgetClass(
                          taskTotal.material.sale -
                          (task.actualMaterialCost || 0)
                        )} text-right`}
                      >
                        {hideActualsInChildRows ? '' : toUSD(
                          taskTotal.material.sale -
                          (task.actualMaterialCost || 0)
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className='p-0'></td>
                      <td className='p-0'></td>
                      <td className='collapse-title text-right'>
                        <span>Resource</span>
                      </td>
                      <td className='text-right'>
                        {toUSD(taskTotal.labor.sale)}
                      </td>
                      <td className='text-right'>
                        {toUSD(taskTotal.labor.cost)}
                      </td>
                      <td className='text-right'>
                        {hideActualsInChildRows ? '' : toUSD(0)}
                      </td>
                      <td className={`text-right`}>
                        {hideActualsInChildRows ? '' : navigateToCostBreakdown ? (
                          <span
                            className='actual-cost-link'
                            onClick={() =>
                              navigateToCostBreakdown('labor', task.id)
                            }
                          >
                            {toUSD(task.actualLaborCost || 0)}
                          </span>
                        ) : (
                          toUSD(task.actualLaborCost || 0)
                        )}
                      </td>
                      <td className='text-right'>
                        {toUSD(taskTotal.labor.profit)}
                      </td>
                      <td
                        className={`${getBudgetClass(
                          taskTotal.labor.sale - (task.actualLaborCost || 0)
                        )} text-right`}
                      >
                        {hideActualsInChildRows ? '' : toUSD(
                          taskTotal.labor.sale - (task.actualLaborCost || 0)
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className='p-0'></td>
                      <td className='p-0'></td>
                      <td className='collapse-title text-right'>
                        <span>Contractor</span>
                      </td>
                      <td className='text-right'>
                        {toUSD(taskTotal.subcontractor.sale)}
                      </td>
                      <td className='text-right'>
                        {toUSD(taskTotal.subcontractor.cost)}
                      </td>
                      <td className='text-right'>
                        {hideActualsInChildRows ? '' : toUSD(task.committedSubContractorCost || 0)}
                      </td>
                      <td className={`text-right`}>
                        {hideActualsInChildRows ? '' : navigateToCostBreakdown ? (
                          <span
                            className='actual-cost-link'
                            onClick={() =>
                              navigateToCostBreakdown('subcontractor', task.id)
                            }
                          >
                            {toUSD(task.actualSubContractorCost || 0)}
                          </span>
                        ) : (
                          toUSD(task.actualSubContractorCost || 0)
                        )}
                      </td>
                      <td className='text-right'>
                        {toUSD(taskTotal.subcontractor.profit)}
                      </td>
                      <td
                        className={`${getBudgetClass(
                          taskTotal.subcontractor.sale -
                          (task.actualSubContractorCost || 0)
                        )} text-right`}
                      >
                        {hideActualsInChildRows ? '' : toUSD(
                          taskTotal.subcontractor.sale -
                          (task.actualSubContractorCost || 0)
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Collapse>
          </td>
        </tr>
      </Fragment>
    );
  };

  const renderNestedStages = (nestedStages) => {
    if (
      nestedStages &&
      Array.isArray(nestedStages) &&
      nestedStages.length > 0
    ) {
      return nestedStages.map((nestedStage) => (
        <StageAccordionBudget
          key={nestedStage.id}
          stage={nestedStage}
          projectQuoteType={projectQuoteType}
          calculations={calculations}
          variations={variations}
          quoteType={quoteType}
          stageId={nestedStage.id}
          progressClaims={progressClaims}
          handleEditClick={handleEditClick}
          navigateToCostBreakdown={navigateToCostBreakdown}
          inProgressQuote={inProgressQuote}
          isGlobalUser={isGlobalUser}
          updatePermission={updatePermission}
          isParentFlagged={isParentFlagged || stage.is_attribution_level}
        />
      ));
    }
    return null;
  };

  /**
   * Renders a section-level breakdown for attribution-flagged sections.
   *
   * FORECAST:
   * - Aggregated totals from all descendant tasks (and approved variations attached to this section)
   *
   * ACTUALS:
   * - Values attributed directly to the flagged section (Timesheet stage attribution for labor)
   */
  const renderFlaggedSectionBreakdown = () => {
    if (!stage?.is_attribution_level || isParentFlagged) return null;

    const sum = (value) => parseFloat(value) || 0;
    const forecastTotals = (aggregatedHeaderTasks || []).reduce(
      (acc, task) => {
        acc.material.sale += sum(task.materialTotal_SaleAmount);
        acc.material.cost += sum(task.materialTotal_Cost);
        acc.labor.sale += sum(task.laborTotal_SaleAmount);
        acc.labor.cost += sum(task.laborTotal_Cost);
        acc.subcontractor.sale += sum(task.subcontractorTotal_SaleAmount);
        acc.subcontractor.cost += sum(task.subcontractorTotal_Cost);
        return acc;
      },
      {
        material: { sale: 0, cost: 0 },
        labor: { sale: 0, cost: 0 },
        subcontractor: { sale: 0, cost: 0 },
      }
    );

    const actuals = {
      material: sum(stage?.attributedActualMaterialCost),
      labor: sum(stage?.attributedActualLaborCost),
      subcontractor: sum(stage?.attributedActualSubContractorCost),
    };

    const committeds = {
      material: sum(stage?.attributedCommittedMaterialCost),
      labor: 0,
      subcontractor: sum(stage?.attributedCommittedSubContractorCost),
    };

    const materialForecastProfit = toDecimal(forecastTotals.material.sale)
      .minus(toDecimal(forecastTotals.material.cost))
      .toNumber();
    const laborForecastProfit = toDecimal(forecastTotals.labor.sale)
      .minus(toDecimal(forecastTotals.labor.cost))
      .toNumber();
    const subcontractorForecastProfit = toDecimal(forecastTotals.subcontractor.sale)
      .minus(toDecimal(forecastTotals.subcontractor.cost))
      .toNumber();

    const materialActualProfit = toDecimal(forecastTotals.material.sale)
      .minus(toDecimal(actuals.material))
      .toNumber();
    const laborActualProfit = toDecimal(forecastTotals.labor.sale)
      .minus(toDecimal(actuals.labor))
      .toNumber();
    const subcontractorActualProfit = toDecimal(forecastTotals.subcontractor.sale)
      .minus(toDecimal(actuals.subcontractor))
      .toNumber();

    return (
      <div className='material-list-block'>
        <div className='table-responsive'>
          {/* Attribution breakdown table - appears directly when section is expanded */}
          <Table
            bordered
            className='table-create table-material table-material-ch table table-bordered budget-table'
            data-cy='budget-flagged-section-breakdown'
          >

            <tbody>
              <tr>
                {/* Match task breakdown alignment: label sits in the 3rd column */}
                <td className='p-0'></td>
                <td className='p-0'></td>
                <td className='collapse-title text-right'>
                  <span>Material</span>
                </td>
                <td className='text-right'>
                  {toUSD(forecastTotals.material.sale)}
                </td>
                <td className='text-right'>
                  {toUSD(forecastTotals.material.cost)}
                </td>
                <td className='text-right'>
                  {toUSD(committeds.material)}
                </td>
                <td className='text-right'>
                  {navigateToCostBreakdown ? (
                    <span
                      className='actual-cost-link'
                      onClick={() =>
                        navigateToCostBreakdown('material', null, stage.id)
                      }
                    >
                      {toUSD(actuals.material)}
                    </span>
                  ) : (
                    toUSD(actuals.material)
                  )}
                </td>
                <td className='text-right'>
                  {toUSD(materialForecastProfit)}
                </td>
                <td
                  className={`${getBudgetClass(
                    materialActualProfit
                  )} text-right`}
                >
                  {toUSD(materialActualProfit)}
                </td>
              </tr>
              <tr>
                <td className='p-0'></td>
                <td className='p-0'></td>
                <td className='collapse-title text-right'>
                  <span>Resource</span>
                </td>
                <td className='text-right'>{toUSD(forecastTotals.labor.sale)}</td>
                <td className='text-right'>{toUSD(forecastTotals.labor.cost)}</td>
                <td className='text-right'>{toUSD(0)}</td>
                <td className='text-right'>
                  {navigateToCostBreakdown ? (
                    <span
                      className='actual-cost-link'
                      onClick={() =>
                        navigateToCostBreakdown('labor', null, stage.id)
                      }
                    >
                      {toUSD(actuals.labor)}
                    </span>
                  ) : (
                    toUSD(actuals.labor)
                  )}
                </td>
                <td className='text-right'>{toUSD(laborForecastProfit)}</td>
                <td
                  className={`${getBudgetClass(
                    laborActualProfit
                  )} text-right`}
                >
                  {toUSD(laborActualProfit)}
                </td>
              </tr>
              <tr>
                <td className='p-0'></td>
                <td className='p-0'></td>
                <td className='collapse-title text-right'>
                  <span>Contractor</span>
                </td>
                <td className='text-right'>
                  {toUSD(forecastTotals.subcontractor.sale)}
                </td>
                <td className='text-right'>
                  {toUSD(forecastTotals.subcontractor.cost)}
                </td>
                <td className='text-right'>
                  {toUSD(committeds.subcontractor)}
                </td>
                <td className='text-right'>
                  {navigateToCostBreakdown ? (
                    <span
                      className='actual-cost-link'
                      onClick={() =>
                        navigateToCostBreakdown('subcontractor', null, stage.id)
                      }
                    >
                      {toUSD(actuals.subcontractor)}
                    </span>
                  ) : (
                    toUSD(actuals.subcontractor)
                  )}
                </td>
                <td className='text-right'>
                  {toUSD(subcontractorForecastProfit)}
                </td>
                <td
                  className={`${getBudgetClass(
                    subcontractorActualProfit
                  )} text-right`}
                >
                  {toUSD(subcontractorActualProfit)}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const hasNestedStages = stage.stages && stage.stages.length > 0;

  return (
    <Accordion key={stage.id} activeKey={openStage ? stage.id : null}>
      <AccordionItem eventKey={stage.id}>
        <AccordionHeader
          onClick={toggleStage}
          className={`${stage.parent_stage_id ? 'remove-padding mh-48' : ''} accordion-icon accordion-budget-icon border projectableContainer justify-content-between tabel-list ${stage.is_attribution_level ? 'reporting-root' : isParentFlagged ? 'non-reporting-section' : ''}`}
        >
          {/* Stage or Section Name */}
          <li className='list-group-item  list-budget-group-item acc-header-text text-truncate me-auto d-flex flex-row align-items-center gap-2 flex-nowrap' data-cy="stage-name">
            {stage.is_attribution_level && !isParentFlagged ? <AssignmentTurnedInIcon fontSize="small" /> : null}
            <span className='text-truncate'>{stage.name}</span>
          </li>

          {/* Completion percentage with value*/}
          <li className={`text-center list-group-item list-budget-group-item completion-percent total-color d-flex flex-column ${isParentFlagged ? 'invisible' : ''}`} data-cy="completion-percentage">
            {!stage.parent_stage_id ? (
              <div className='fst-12' data-cy="label">Completion %</div>
            ) : (
              ''
            )}
            <div className='mt-2' data-cy="value">
              {Math.min(roundValue((totalHeaderMetrics.claimAmount / totalHeaderMetrics.total * 100) || 0, 0), 100)}%
            </div>
          </li>

          {/* Claimed */}
          <li className={`text-right list-group-item list-budget-group-item total-color d-flex flex-column ${isParentFlagged ? 'invisible' : ''}`} data-cy="claim-amount">
            {!stage.parent_stage_id ? (
              <div className='text-right fst-12' data-cy='label'>Claimed</div>
            ) : (
              ''
            )}
            <div className='text-right mt-2' data-cy='value'>
              {toUSD(totalHeaderMetrics.claimAmount)}
            </div>
          </li>
          <li className='text-right list-group-item list-budget-group-item total-color d-flex flex-column' data-cy="sale-amount">
            {!stage.parent_stage_id ? (
              <div className='text-right fst-12' data-cy='label'>Sale Amount</div>
            ) : (
              ''
            )}
            <div className='text-right mt-2' data-cy='value'>
              {toUSD(totalHeaderMetrics.total)}
            </div>
          </li>
          <li className='text-right ms-1 list-group-item list-budget-group-item total-color d-flex flex-column' data-cy="forecast-cost">
            {!stage.parent_stage_id ? (
              <div className='text-right fst-12' data-cy='label'>Forecast Cost</div>
            ) : (
              ''
            )}
            <div className='text-right mt-2' data-cy='value'>
              {toUSD(totalHeaderMetrics.subtotal)}
            </div>
          </li>
          <li className={`text-right ms-1 list-group-item list-budget-group-item total-color d-flex flex-column ${isParentFlagged ? 'invisible' : ''}`} data-cy="committed-cost">
            {!stage.parent_stage_id ? (
              <div className='text-right fst-12' data-cy='label'>Committed Cost</div>
            ) : (
              ''
            )}
            <div className='text-right mt-2' data-cy='value'>
              {toUSD(attributionAwareHeaderCommittedCost)}
            </div>
          </li>
          <li className={`text-right ms-1 list-group-item list-budget-group-item total-color d-flex flex-column ${isParentFlagged ? 'invisible' : ''}`} data-cy="actual-cost">
            {!stage.parent_stage_id ? (
              <div className='text-right fst-12' data-cy='label'>Actual Cost</div>
            ) : (
              ''
            )}
            <div className='text-right mt-2' data-cy='value'>
              {stage.is_attribution_level && navigateToCostBreakdown ? (
                <span
                  className='actual-cost-link'
                  onClick={() =>
                    navigateToCostBreakdown('all', null, stage.id)
                  }
                >
                  {toUSD(attributionAwareHeaderActualCost)}
                </span>
              ) : (
                toUSD(attributionAwareHeaderActualCost)
              )}
            </div>
          </li>
          <li className='text-right list-group-item list-budget-group-item total-color d-flex flex-column' data-cy="gross-profit-forecast">
            {!stage.parent_stage_id ? (
              <div className='text-right fst-12' data-cy='label'>Profit (Forecast)</div>
            ) : (
              ''
            )}
            <div className='text-right mt-1' data-cy='value'>
              <div>{toUSD(totalHeaderMetrics.total_profit)}</div>
              <div className='profit-percent fst-12'>({toPercent(totalHeaderMetrics.total_profit, totalHeaderMetrics.subtotal)})</div>
            </div>
          </li>
          <li className={`text-right list-group-item list-budget-group-item total-color d-flex flex-column ${isParentFlagged ? 'invisible' : ''}`} data-cy="gross-profit-actual">
            {!stage.parent_stage_id ? (
              <div className='text-right fst-12' data-cy='label'>Profit (Actual)</div>
            ) : (
              ''
            )}
            <div className='text-right mt-1' data-cy='value'>
              <div>{toUSD(getClaimProfitAmount(totalHeaderMetrics.claimAmount, attributionAwareHeaderActualCost))}</div>
              <div className='profit-percent fst-12'>{getClaimProfitPct(totalHeaderMetrics.claimAmount, attributionAwareHeaderActualCost)}</div>
            </div>
          </li>
        </AccordionHeader>
        <AccordionBody className={`${hasNestedStages ? 'p-0 ps-2' : 'p-0'}`}>
          {(openStage || hasBeenOpened) && (
            hasNestedStages ? (
            <>
              {renderFlaggedSectionBreakdown()}
              {renderNestedStages(stage.stages)}
            </>
          ) : (
            <div className='purchase-order-block'>
              {renderFlaggedSectionBreakdown()}
              <div className='row'>
                <div className='col-12'>
                  <div className='material-list-block'>
                    <div className='table-responsive'>
                      <table
                        className='table-create table-material table-material-ch custom-budget-table budget-table table table-bordered'
                        data-cy={'budget-task-table'}
                      >
                        <thead>
                          <tr>
                            <th>Task</th>
                            <th className={`${hideActualsInChildRows ? 'invisible' : ''}`}>Completion %</th>
                            <th className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`}>Claimed</th>
                            <th className='text-right'>Sale Amount</th>
                            <th className='text-right'>Forecast Cost</th>
                            <th className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`}>Committed Cost</th>
                            <th className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`}>Actual Cost</th>
                            <th className='text-right'>
                              Profit (Forecast)
                            </th>
                            <th className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`}>
                              Profit (Actual)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {aggregatedTasks.map((task) => renderTaskRow(task))}
                          <tr className='budget-total'>
                            <td></td>
                            <td>
                              <b>Total</b>
                            </td>
                            <td className={`text-right`}>
                              {hideActualsInChildRows ? '' : toUSD(totalMetrics.claimAmount)}
                            </td>
                            <td className='text-right'>
                              {toUSD(totalMetrics.total)}
                            </td>
                            <td className='text-right'>
                              {toUSD(totalMetrics.subtotal)}
                            </td>
                            <td className={`text-right`}>
                              {hideActualsInChildRows ? '' : toUSD(totalMetrics.committedCost)}
                            </td>
                            <td className={`text-right`}>
                              {hideActualsInChildRows ? '' : toUSD(totalMetrics.actualCost)}
                            </td>
                            <td className='text-right'>
                              <div>{toUSD(totalMetrics.total_profit)}</div>
                              <div className='profit-percent fst-12'>({toPercent(totalMetrics.total_profit, totalMetrics.subtotal)})</div>
                            </td>
                            <td className={`text-right`}>
                              {hideActualsInChildRows ? '' : (
                                <>
                                  <div>{toUSD(getClaimProfitAmount(totalMetrics.claimAmount, totalMetrics.actualCost))}</div>
                                  <div className='profit-percent fst-12'>{getClaimProfitPct(totalMetrics.claimAmount, totalMetrics.actualCost)}</div>
                                </>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Variations Section */}
              {variationsForStage && variationsForStage.length > 0 && (() => {
                const variationTotalMetrics = calculateTotalMetrics(variationsForStage);
                return (
                  <div className='row'>
                    <div className='col-12'>
                      <div className='material-list-block'>
                        <div className='table-responsive'>
                          <table
                            className='table-create table-material custom-budget-table  table-material-ch budget-table table table-bordered'
                            data-cy={'budget-task-table'}
                          >
                            <thead>
                              <tr>
                                <th>Variations</th>
                                <th className={`${hideActualsInChildRows ? 'invisible' : ''}`}>Completion %</th>
                                <th className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`}>Claimed</th>
                                <th className='text-right'>Sale Amount</th>
                                <th className='text-right'>Forecast Cost</th>
                                <th className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`}>Committed Cost</th>
                                <th className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`}>Actual Cost</th>
                                <th className='text-right'>
                                  Profit (Forecast)
                                </th>
                                <th className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`}>
                                  Profit (Actual)
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {variationsForStage.map((variation) =>
                                renderTaskRow(variation)
                              )}
                              <tr className='budget-total'>
                                <td></td>
                                <td>
                                  <b>Total</b>
                                </td>
                                <td className={`text-right ${hideActualsInChildRows ? 'invisible' : ''}`}>
                                  {toUSD(variationTotalMetrics.claimAmount)}
                                </td>
                                <td className='text-right'>
                                  {toUSD(variationTotalMetrics.total)}
                                </td>
                                <td className='text-right'>
                                  {toUSD(variationTotalMetrics.subtotal)}
                                </td>
                                <td className={`text-right`}>
                                  {hideActualsInChildRows ? '' : toUSD(variationTotalMetrics.actualCost)}
                                </td>
                                <td className='text-right'>
                                  <div>{toUSD(variationTotalMetrics.total_profit)}</div>
                                  <div className='profit-percent fst-12'>({toPercent(variationTotalMetrics.total_profit, variationTotalMetrics.subtotal)})</div>
                                </td>
                                <td className={`text-right`}>
                                  {hideActualsInChildRows ? '' : (
                                    <>
                                      <div>{toUSD(getClaimProfitAmount(variationTotalMetrics.claimAmount, variationTotalMetrics.actualCost))}</div>
                                      <div className='profit-percent fst-12'>{getClaimProfitPct(variationTotalMetrics.claimAmount, variationTotalMetrics.actualCost)}</div>
                                    </>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              </div>
            )
          )}
        </AccordionBody>
      </AccordionItem>
    </Accordion>
  );
};

export default StageAccordionBudget;
